import {
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { CentsToEurosPipe } from '../../../pipes/cents-to-euros/cents-to-euros.pipe';
import { ChipComponent } from '../../data-display/chip/chip.component';
import { DatePipe } from '@angular/common';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { FormArray, FormGroup } from '@angular/forms';
import { ITicketOwnerDataForm } from '../../../pages/shop/direct-ticket-configuration-page/direct-ticket-owner-data-page/direct-ticket-owner-data-page.component';
import { ITicketPersonalDataForm } from '../../../pages/shop/direct-ticket-configuration-page/direct-ticket-personal-data-page/direct-ticket-personal-data-page.component';
import { ShoppingCartService } from '../../../services/shopping-cart/shopping-cart.service';
import { DirectTicketShoppingCartSubMenuComponent } from '../direct-ticket-shopping-cart-sub-menu/direct-ticket-shopping-cart-sub-menu.component';
import {
  ERelationType,
  ITicket,
  TariffProductDefinition,
  TariffSelectionAttribute,
  NamedTariffProductDefinition,
  EEntityType,
  DATE_FORMAT,
  ViaName,
  ICompanionData,
} from '@web/dtos';
import { CloseOnOffClick } from '../../../directives/close-on-off-click';
import { EShoppingCartMenuAction } from '../direct-ticket-configuration-shopping-cart/direct-ticket-configuration-shopping-cart.component';
import {
  ITicketInformation,
  DirectTicketInformationDialogComponent,
} from '../../dialogs/direct-ticket-information-dialog/direct-ticket-information-dialog.component';
import { DirectTicketRemoveFromShoppingCartDialog } from '../../dialogs/direct-ticket-remove-from-shopping-cart-dialog/direct-ticket-remove-from-shopping-cart-dialog.component';
import { DirectTicketEditOwnerShoppingCartDialogComponent } from '../../dialogs/direct-ticket-edit-owner-shopping-cart-dialog/direct-ticket-edit-owner-shopping-cart-dialog.component';
import { DirectTicketEditOptionsShoppingCartDialogComponent } from '../../dialogs/direct-ticket-edit-options-shopping-cart-dialog/direct-ticket-edit-options-shopping-cart-dialog.component';
import { ActiveDirectTicketStore } from '../../../services/api/direct-ticket/active/active-direct-ticket-store.service';
import { ITicketDefinitionForm } from '../../../services/shopping-cart/shopping-cart-form.service';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { DecimalsToCentsPipe } from '../../../pipes/decimals-to-cents/decimals-to-cents.pipe';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { ViaListComponent } from '../../data-display/via-list/via-list.component';

@Component({
  selector: 'aw-direct-ticket-shopping-cart-item',
  imports: [
    AppendTestIdPipe,
    CentsToEurosPipe,
    ChipComponent,
    DatePipe,
    DecimalsToCentsPipe,
    FilterTextsPipe,
    LanguagePipe,
    DirectTicketShoppingCartSubMenuComponent,
    ViaListComponent,
    CloseOnOffClick,
  ],
  templateUrl: './direct-ticket-shopping-cart-item.component.html',
})
export class DirectTicketShoppingCartItemComponent {
  public readonly positionInCart: InputSignal<number> = input.required();
  public readonly productForm: InputSignal<FormGroup<ITicketDefinitionForm>> =
    input.required();
  public readonly ticket: InputSignal<ITicket> = input.required();
  public readonly ticketName: InputSignal<string> = input.required();
  public readonly amount: InputSignal<number> = input.required();
  public readonly ticketAttributes: InputSignal<string[]> = input.required();
  public readonly travelDate: InputSignal<Date> = input.required();
  public readonly owner: InputSignal<
    FormArray<FormGroup<ITicketOwnerDataForm>>
  > = input.required();
  public readonly companions: InputSignal<ICompanionData[]> = input.required();
  public readonly isTicketOwnerDataRequired: InputSignal<boolean> =
    input.required();
  protected readonly isGroupTicket: Signal<boolean> = computed(() => {
    const maxNumberOfPeople =
      this.productForm().controls.product.value?.maxNumberOfPeople ?? 1;

    return maxNumberOfPeople > 1;
  });
  protected readonly amountOfPeople: WritableSignal<number | undefined> =
    signal(undefined);

  private readonly dialogService: DialogService = inject(DialogService);
  private readonly directTicketStore: ActiveDirectTicketStore = inject(
    ActiveDirectTicketStore
  );
  protected readonly shoppingCartService: ShoppingCartService =
    inject(ShoppingCartService);
  protected readonly textStore: WebTextStore = inject(WebTextStore);
  protected readonly isSubMenuVisible: WritableSignal<boolean> = signal(false);
  protected readonly personalDataForm: FormGroup<ITicketPersonalDataForm> =
    this.shoppingCartService.personalDataForm;

  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly DATE_FORMAT: string = DATE_FORMAT;

  constructor() {
    effect(onCleanup => {
      const amountOfPeopleControl = this.productForm().controls.amountOfPeople;
      const updateAmountOfPeople = (value: number): void => {
        this.amountOfPeople.set(
          typeof value === 'number' && Number.isFinite(value)
            ? value
            : undefined
        );
      };

      updateAmountOfPeople(amountOfPeopleControl.value);

      const subscription =
        amountOfPeopleControl.valueChanges.subscribe(updateAmountOfPeople);
      onCleanup(() => subscription.unsubscribe());
    });
  }

  protected readonly productDefinition: Signal<TariffProductDefinition> =
    computed((): TariffProductDefinition => {
      return this.productForm()!.value.product!;
    });
  protected readonly alteredOwners: Signal<
    FormArray<FormGroup<ITicketOwnerDataForm>>['value']
  > = computed((): FormArray<FormGroup<ITicketOwnerDataForm>>['value'] => {
    const personalData = this.personalDataForm.getRawValue();
    return this.owner()
      .getRawValue()
      .filter(
        owner =>
          !owner.isForUser ||
          (owner.firstName &&
            owner.firstName === personalData.firstName &&
            owner.lastName &&
            owner.lastName === personalData.lastName)
      );
  });

  protected showSubMenu(): void {
    this.isSubMenuVisible.set(true);
  }
  protected hideSubMenu(): void {
    this.isSubMenuVisible.set(false);
  }

  protected viaToArray(product: TariffProductDefinition): ViaName[] {
    return product.via!.split(',') as ViaName[];
  }

  protected handleSubMenuClick(
    action: EShoppingCartMenuAction,
    index: number
  ): void {
    switch (action) {
      case EShoppingCartMenuAction.editOwner:
        this.openOwnerEdit(this.ticket(), this.productDefinition(), index);
        break;
      case EShoppingCartMenuAction.editOptions:
        this.openTicketEdit(
          this.ticket(),
          this.productDefinition(),
          this.directTicketStore
            .tickets()
            .find(ticket => ticket.name === this.ticketName())
            ?.selectionAttributes ?? [],
          index
        );
        break;
      case EShoppingCartMenuAction.info:
        this.openDetails(this.ticket(), this.productDefinition());
        break;
      case EShoppingCartMenuAction.delete:
        this.openTicketDelete(this.ticket(), this.productDefinition(), index);
        break;
    }
    this.hideSubMenu();
  }

  private openDetails(ticket: ITicket, product: TariffProductDefinition): void {
    this.dialogService.openDialog<ITicketInformation>(
      DirectTicketInformationDialogComponent,
      {
        headline: '',
        width: '400px',
        index: this.positionInCart(),
        ticket,
        name: this.ticketName(),
        productDefinition: product,
      }
    );
  }

  private openTicketEdit(
    ticket: ITicket,
    product: TariffProductDefinition,
    ticketAttributes: TariffSelectionAttribute[],
    index: number
  ): void {
    this.dialogService.openDialog<{
      index: number;
      name: string;
      ticket: ITicket;
      selectionAttributes: TariffSelectionAttribute[];
      productSortOptions: TariffSelectionAttribute[];
      productDefinition: TariffProductDefinition;
    }>(DirectTicketEditOptionsShoppingCartDialogComponent, {
      headline: '',
      width: '800px',
      ticket,
      name: this.ticketName(),
      productDefinition: {
        name: this.ticketName(),
        ...product,
      } as NamedTariffProductDefinition,
      selectionAttributes: ticketAttributes.filter(value => {
        return value.name.toLowerCase() !== 'name';
      }),
      productSortOptions: ticketAttributes.filter(value => {
        return value.name.toLowerCase() === 'name';
      }),
      index,
    });
  }

  private openOwnerEdit(
    ticket: ITicket,
    product: TariffProductDefinition,
    index: number
  ): void {
    this.dialogService.openDialog<{
      index: number;
      name: string;
      ticket: ITicket;
      productDefinition: TariffProductDefinition;
    }>(DirectTicketEditOwnerShoppingCartDialogComponent, {
      headline: '',
      width: '400px',
      ticket,
      name: this.ticketName(),
      productDefinition: {
        name: this.ticketName(),
        ...product,
      } as NamedTariffProductDefinition,
      index,
    });
  }

  private openTicketDelete(
    ticket: ITicket,
    product: TariffProductDefinition,
    index: number
  ): void {
    this.dialogService.openDialog<{
      index: number;
      name: string;
      ticket: ITicket;
      productDefinition: TariffProductDefinition;
    }>(DirectTicketRemoveFromShoppingCartDialog, {
      headline: '',
      width: '400px',
      ticket,
      name: this.ticketName(),
      productDefinition: {
        name: this.ticketName(),
        ...product,
      } as NamedTariffProductDefinition,
      index,
    });
  }
}
